import React, {
  useContext,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Field } from 'react-final-form';

import {
  Accordion,
  Col,
  Row,
  Select,
  TextArea,
  TextField,
} from '@folio/stripes/components';

import { ViewMetaData } from '@folio/stripes/smart-components';

import {
  DESCRIPTION_LIMIT_MAX,
  NAME_LIMIT_MAX,
  TASK_FIELDS,
  TASK_STATUS_INITIAL_VALUES,
  TASK_PRIORITY_INITIAL_VALUES,
  STATUSES_FILTER_PARAMETER,
} from '../../../../../constants';

import {
  composeValidators,
  getDropdownOptionsUtils,
  validateFieldLength,
  validateRequiredField,
} from '../../../../../utils';
import { StartDueDateRange } from '../../../../common';
import { DataFetcherContext } from '../../../../../routes/root-layer';

const validateTaskNameLength = value => validateFieldLength(value, NAME_LIMIT_MAX);
const validateDescriptionLength = value => validateFieldLength(value, DESCRIPTION_LIMIT_MAX);

const GeneralInformation = ({
  metadata,
}) => {
  const {
    taskStatuses,
    priorities,
  } = useContext(DataFetcherContext);

  const statusOptions = useMemo(() => {
    return getDropdownOptionsUtils(`task.${STATUSES_FILTER_PARAMETER}`, taskStatuses || TASK_STATUS_INITIAL_VALUES);
  }, [taskStatuses]);
  const priorityOptions = useMemo(() => {
    return getDropdownOptionsUtils(TASK_FIELDS.PRIORITY, priorities || TASK_PRIORITY_INITIAL_VALUES);
  }, [priorities]);

  const validateTaskName = useMemo(() => composeValidators(validateRequiredField, validateTaskNameLength), []);

  return (
    <Accordion
      id="general"
      label={<FormattedMessage id="ui-task-list.title.accordion.generalInformation" />}
    >
      {
        metadata && (
          <ViewMetaData
            headingLevel={3}
            metadata={metadata}
          />)
      }
      <Row>
        <Col
          data-test-task-name
          sm={3}
        >
          <Field
            required
            component={TextField}
            id={TASK_FIELDS.NAME}
            label={<FormattedMessage id="ui-task-list.task.field.name" />}
            name={TASK_FIELDS.NAME}
            type="text"
            validate={validateTaskName}
          />
        </Col>
        <Col
          data-test-task-status
          sm={3}
        >
          <Field
            component={Select}
            id={TASK_FIELDS.STATUS}
            label={<FormattedMessage id="ui-task-list.task.field.status" />}
            name={TASK_FIELDS.STATUS}
            type="text"
          >
            {statusOptions}
          </Field>
        </Col>
        <Col
          data-test-task-priority
          sm={3}
        >
          <Field
            component={Select}
            id={TASK_FIELDS.PRIORITY}
            label={<FormattedMessage id="ui-task-list.task.field.priority" />}
            name={TASK_FIELDS.PRIORITY}
            type="text"
          >
            {priorityOptions}
          </Field>
        </Col>
      </Row>
      <Row>
        <Col sm={6}>
          <StartDueDateRange
            dueDateTranslationKey="ui-task-list.task.field.dueDate"
            startDateTranslationKey="ui-task-list.task.field.startDate"
          />
        </Col>
      </Row>
      <Row>
        <Col
          data-test-task-description
          sm={12}
        >
          <Field
            component={TextArea}
            id={TASK_FIELDS.DESCRIPTION}
            label={<FormattedMessage id="ui-task-list.task.field.description" />}
            name={TASK_FIELDS.DESCRIPTION}
            type="text"
            validate={validateDescriptionLength}
          />
        </Col>
      </Row>
    </Accordion>
  );
};

GeneralInformation.propTypes = {
  metadata: PropTypes.object,
};

GeneralInformation.defaultProps = {
  metadata: null
};

export default GeneralInformation;
